import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import {
  S3ResponseType,
  uploadToS3,
} from 'src/components/molecules/AttachmentUploader/services/fileUpload';

interface Attachment {
  readonly id: string;
  readonly name: string;
  readonly uploaded: boolean;
}

export class InitialAttachment implements Attachment {
  readonly id: string;
  readonly name: string;
  readonly uploaded: boolean;

  constructor(id: string, name: string) {
    this.name = name;
    this.id = id;
    this.uploaded = true;
  }
}

interface UploadError {
  readonly attachmentId: string;
  readonly fileName: string;
  readonly message: string;
}

const BYTES_IN_MB = 1048576;

const findAttachment = (attachmentId: string, attachments: Attachment[]) => {
  return attachments.findIndex((attachment) => {
    return attachment.id === attachmentId;
  });
};

const removeAttachment = (
  attachmentId: string,
  attachments: Attachment[],
  setAttachments: (attachments: Attachment[]) => void,
) => {
  setAttachments(attachments.filter((attachment) => attachment.id !== attachmentId));
};

const uploadDisabled = (maxUploads: number, attachments: Attachment[]): boolean => {
  if (maxUploads > 0 && attachments.length == maxUploads) {
    return true;
  }
  return false;
};

const markAttachmentUploaded = (
  attachmentId: string,
  attachments: Attachment[],
  setAttachments: (attachments: Attachment[]) => void,
) => {
  const attachmentIndex = findAttachment(attachmentId, attachments);

  if (attachmentIndex !== -1) {
    const attachedFiles = [...attachments];
    const { name } = attachments[attachmentIndex];
    attachedFiles[attachmentIndex] = { id: attachmentId, name, uploaded: true };
    setAttachments(attachedFiles);
  }
};

export interface UseAttachmentUploadData {
  attachments: Attachment[];
  deleteUploadedFile: (attachmentId: string) => void;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isUploadDisabled: () => boolean;
  uploadError: UploadError | null;
}

export const useAttachmentUpload = (
  maxUploads: number,
  mimeTypes: string[],
  maxFileSize: number,
  initialAttachments: InitialAttachment[],
  shouldUploadAttachment: (fileName: string) => string | null,
  onUploadStarted: (attachmentId: string, fileName: string) => void,
  onUploadError: (attachementId: string, fileName: string) => void,
  onUploadCompleted: (attachmentId: string, fileName: string, uploadReference: string) => void,
  onAttachmentRemoved: (attachmentId: string) => void,
): UseAttachmentUploadData => {
  const [attachments, setAttachments] = useState<Array<Attachment>>(initialAttachments);
  const [uploadError, setUploadError] = useState<UploadError | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (uploadError) {
      removeAttachment(uploadError.attachmentId, attachments, setAttachments);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadError]);

  useEffect(() => {
    if (uploadSuccess) {
      markAttachmentUploaded(uploadSuccess, attachments, setAttachments);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadSuccess]);

  const deleteUploadedFile = (attachmentId: string) => {
    removeAttachment(attachmentId, attachments, setAttachments);
    onAttachmentRemoved(attachmentId);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setUploadError(null);
    const fileObj = event.target.files && event.target.files[0];
    if (!fileObj) {
      return;
    }

    const { name: fileName, type: fileType } = fileObj;

    const attachmentId = uuidv4();

    if (mimeTypes.length && !mimeTypes.includes(fileType)) {
      const error = `File '${fileObj.name}' is the wrong type (${fileType})`;
      setUploadError({ message: error, fileName, attachmentId });
      return;
    }

    if (maxFileSize && fileObj.size > maxFileSize) {
      const error = `File '${fileObj.name}' is too big. Max is ${Math.floor(
        maxFileSize / BYTES_IN_MB,
      )}mb`;
      setUploadError({ message: error, fileName, attachmentId });
      return;
    }

    event.target.value = '';

    const shouldUploadErrorResult = shouldUploadAttachment(fileObj.name);

    if (shouldUploadErrorResult) {
      setUploadError({ message: shouldUploadErrorResult, fileName, attachmentId });
      return;
    }

    setAttachments([...attachments, { id: attachmentId, name: fileName, uploaded: false }]);
    onUploadStarted(attachmentId, fileName);
    const s3Response = await uploadToS3(fileType, fileObj);
    switch (s3Response.type) {
      case S3ResponseType.ERROR: {
        setUploadError({
          message: `Error uploading '${fileName}', please try again`,
          fileName,
          attachmentId,
        });
        onUploadError(attachmentId, fileName);
        break;
      }
      case S3ResponseType.SUCCESS: {
        setUploadSuccess(attachmentId);
        onUploadCompleted(attachmentId, fileName, s3Response.uploadReference);
        break;
      }
    }
  };

  return {
    attachments,
    uploadError,
    handleFileChange,
    deleteUploadedFile,
    isUploadDisabled: () => uploadDisabled(maxUploads, attachments),
  };
};
